import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Table as LegoTable } from '@lucid/lego';
import { useStore } from '../../../../../../../store/store';
import { createColumns } from './utils';
import { Quote } from 'src/api/services/lupp/quotes/dto.quotes';
import { useDownloadQuote } from '../../../../../../../api/services/lupp/quotes/quotes';

type TableProps = {
  theme: 'light' | 'dark';
  data: Quote[];
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  prospectId: string;
  onQuoteClick: (quoteId: string) => void;
  locale?: string;
};

/**
 * Quotes Table component
 * @component
 * @param props - Table props
 * @param props.theme - Theme variant (light/dark)
 * @param props.data - Array of quote data
 * @param props.labels - Labels for i18n
 * @param props.searchQuery - Current search query
 * @param props.onSearchChange - Search change handler
 * @param props.prospectId - The prospect identifier
 * @returns Table component
 * @example
 * <Table
 *   theme="dark"
 *   data={quotes}
 *   labels={labels}
 *   searchQuery={searchQuery}
 *   onSearchChange={handleSearchChange}
 *   prospectId="123"
 * />
 */
const Table: React.FC<TableProps> = ({
  theme,
  data,
  labels,
  searchQuery,

  onSearchChange: _onSearchChange,
  prospectId,
  onQuoteClick,
  locale = 'en-US',
}) => {
  const setSelectedQuote = useStore((state) => state.setSelectedQuote);
  const [downloadQuoteId, setDownloadQuoteId] = useState<string | null>(null);

  const { data: downloadData, isFetching: isDownloading } = useDownloadQuote({
    prospectId,
    quoteId: downloadQuoteId ?? '',
    language: locale.split('-')[0],
    enabled: !!downloadQuoteId,
  });

  // Handle download when data is received
  useEffect(() => {
    if (!downloadData || !downloadQuoteId) return;

    const { blob, filename } = downloadData;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    setDownloadQuoteId(null);
  }, [downloadData, downloadQuoteId]);

  const handleQuoteClick = useCallback(
    (record: Quote) => {
      setSelectedQuote(record);
      onQuoteClick(record.quote_identifier);
    },
    [setSelectedQuote, onQuoteClick]
  );

  const handleDownloadClick = useCallback((record: Quote) => {
    setDownloadQuoteId(record.quote_identifier);
  }, []);

  const columns = useMemo(
    () =>
      createColumns({
        theme,
        onQuoteClick: handleQuoteClick,
        onDownloadClick: handleDownloadClick,
        labels,
        isDownloading,
        downloadingQuoteId: downloadQuoteId,
        locale,
      }),
    [theme, handleQuoteClick, handleDownloadClick, labels, isDownloading, downloadQuoteId, locale]
  );

  return (
    <LegoTable
      columns={columns}
      data={data}
      theme={theme === 'light' ? 'dark' : 'light'}
      pagination={{
        enabled: true,
        pageSize: 10,
      }}
      labels={{
        showingItems:
          labels?.['configurator.table.showingItems'] ??
          'Showing {{start}}-{{end}} of {{total}} Items',
      }}
      emptyState={{
        message: searchQuery.trim()
          ? labels?.['no_results_found'] || 'No results found'
          : labels?.['no_quotes_available'] || 'No quotes yet for this prospect.',
      }}
      gaComponent="quotes-table"
      dataTestId="quotes-table"
      selection={{ enabled: false }}
    />
  );
};

Table.displayName = 'QuotesTable';

export default Table;
